import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import BankDetailsCard from "../components/BankDetailsCard";
import YieldCalculatorCard from "../components/YieldCalculatorCard";
import ManagementTeamSection from "../components/ManagementTeamSection";
import KycProgressCard from "../components/KycProgressCard";
import { api, formatCurrencyEUR, formatDate, formatNumber } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/LanguageContext";

export default function DashboardPage() {
  const { user } = useAuth();
  const { locale, t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    Promise.all([api.get("/dashboard/summary"), api.get("/deposits/me")])
      .then(([s, d]) => {
        setSummary(s.data);
        setDeposits(d.data);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    const tk = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tk);
  }, []);

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const secondsInDay = (now - startOfToday) / 1000;
  const fractionOfDay = Math.min(1, secondsInDay / 86400);
  const todayInterest = summary ? summary.totals.daily_net * fractionOfDay : 0;

  const firstName = (user?.name || "").split(" ")[0] || t("dash.greeting_default");
  const greeting = t("dash.greeting", { name: firstName });

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="dashboard-page">
        <div className="br-fade-up">
          <div className="br-overline" data-testid="dashboard-overline">{t("dash.overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.8rem)] mt-3" data-testid="dashboard-greeting">
            {greeting}
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            {t("dash.intro")}
          </p>
        </div>

        {error && (
          <div className="mt-8 br-card p-4 text-[color:var(--br-red)]" data-testid="dashboard-error">
            {String(error)}
          </div>
        )}

        <KycProgressCard />

        {!summary ? (
          <div className="mt-12 font-mono-fin text-xs tracking-[0.32em] uppercase text-[color:var(--br-text-dim)]">
            {t("dash.loading")}
          </div>
        ) : (
          <>
            {/* Hero metric */}
            <div
              className="mt-12 br-card br-aurora-ribbon relative grid md:grid-cols-2 gap-0 br-fade-up br-delay-1"
              data-testid="dashboard-hero-card"
            >
              <div className="px-8 py-10 border-b md:border-b-0 md:border-r border-[color:var(--br-line)]">
                <div className="br-overline">{t("dash.capital_managed")}</div>
                <div
                  className="font-serif-display text-[clamp(2.8rem,5vw,4.2rem)] text-[color:var(--br-parchment)] mt-4 leading-none font-mono-fin"
                  data-testid="hero-total-principal"
                >
                  {formatCurrencyEUR(summary.totals.principal, locale)}
                </div>
                <div className="text-[0.85rem] text-[color:var(--br-text-dim)] mt-4 leading-relaxed">
                  {summary.deposit_count === 1
                    ? t("dash.on_n_deposits_one", { n: 1 })
                    : t("dash.on_n_deposits", { n: summary.deposit_count })}{" "}
                  {t("dash.updated_at", { date: formatDate(summary.as_of, locale) })}
                </div>
              </div>
              <div className="px-8 py-10" style={{ background: "linear-gradient(180deg, rgba(167,128,73,0.06), transparent)" }}>
                <div className="br-overline">{t("dash.today_accrued")}</div>
                <div
                  className="font-serif-display text-[clamp(2.8rem,5vw,4.2rem)] mt-4 leading-none font-mono-fin"
                  style={{ color: "var(--br-bronze-soft)" }}
                  data-testid="hero-today-interest"
                >
                  + {formatCurrencyEUR(todayInterest, locale)}
                </div>
                <div className="text-[0.85rem] text-[color:var(--br-text-dim)] mt-4 leading-relaxed">
                  {t("dash.linear_accrual")} ·{" "}
                  <span className="text-[color:var(--br-bronze)] font-mono-fin">
                    {t("dash.per_day", { amount: formatCurrencyEUR(summary.totals.daily_net, locale) })}
                  </span>
                </div>
              </div>
            </div>

            {/* KPI strip */}
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 br-fade-up br-delay-2" data-testid="dashboard-kpis">
              <KPI label={t("dash.kpi_accrued")} value={formatCurrencyEUR(summary.totals.accrued_net_to_date, locale)} accent testid="kpi-accrued" />
              <KPI label={t("dash.kpi_monthly")} value={formatCurrencyEUR(summary.totals.monthly_estimated_net, locale)} testid="kpi-monthly" />
              <KPI label={t("dash.kpi_yearly")} value={formatCurrencyEUR(summary.totals.yearly_estimated_net, locale)} testid="kpi-yearly" />
              <KPI label={t("dash.kpi_statements")} value={String(summary.statements_count)} testid="kpi-statements" />
            </div>

            {/* Next coupon */}
            {summary.next_coupon && (
              <div className="mt-8 br-card p-8 grid sm:grid-cols-3 gap-6 items-end br-fade-up br-delay-3" data-testid="next-coupon-card">
                <div>
                  <div className="br-overline">{t("dash.next_coupon")}</div>
                  <div className="font-serif-display text-[2.2rem] text-[color:var(--br-parchment)] mt-3">
                    {formatDate(summary.next_coupon.date, locale)}
                  </div>
                  <div className="text-[0.85rem] text-[color:var(--br-text-dim)] mt-2">
                    {summary.next_coupon.label}
                  </div>
                </div>
                <div>
                  <div className="br-overline">{t("dash.principal")}</div>
                  <div className="font-mono-fin text-[1.6rem] text-[color:var(--br-parchment)] mt-3">
                    {formatCurrencyEUR(summary.next_coupon.principal, locale)}
                  </div>
                </div>
                <div>
                  <div className="br-overline">{t("dash.coupon_amount")}</div>
                  <div className="font-mono-fin text-[1.8rem] mt-3" style={{ color: "var(--br-bronze)" }}>
                    + {formatCurrencyEUR(summary.next_coupon.amount_net, locale)}
                  </div>
                </div>
              </div>
            )}

            {/* Deposits preview */}
            <div className="mt-14 br-fade-up br-delay-4">
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-serif-display text-[2rem]">{t("dash.your_deposits")}</h2>
                <Link
                  to="/positions"
                  className="font-mono-fin text-[0.7rem] tracking-[0.2em] uppercase text-[color:var(--br-bronze-soft)] hover:text-[color:var(--br-bronze)]"
                  data-testid="view-all-deposits-link"
                >
                  {t("dash.see_all")}
                </Link>
              </div>
              <div className="grid lg:grid-cols-2 gap-5" data-testid="dashboard-deposits-grid">
                {deposits.slice(0, 4).map((d) => (
                  <DepositTile key={d.id} d={d} locale={locale} t={t} />
                ))}
              </div>
            </div>

            {/* Bank details for incoming wires */}
            <BankDetailsCard bankDetails={user?.bank_details} />

            {/* Yield calculator — visible only to initial clients (no active deposits). */}
            <YieldCalculatorCard />

            {/* Management team — gated by full KYC approval. */}
            <ManagementTeamSection />
          </>
        )}
      </section>
    </PortalLayout>
  );
}

function KPI({ label, value, accent = false, testid }) {
  return (
    <div className="br-card p-6" data-testid={testid}>
      <div className="br-overline">{label}</div>
      <div
        className={`font-mono-fin mt-3 text-[1.5rem] ${accent ? "" : "text-[color:var(--br-parchment)]"}`}
        style={accent ? { color: "var(--br-bronze)" } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

function DepositTile({ d, locale, t }) {
  const pct = Math.round((d.days_elapsed / d.duration_days) * 100);
  return (
    <div className="br-card p-7" data-testid={`deposit-tile-${d.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="br-overline">{d.label || t("dash.deposit")}</div>
          <div className="font-serif-display text-[1.6rem] text-[color:var(--br-parchment)] mt-2">
            {formatCurrencyEUR(d.principal, locale)}
          </div>
        </div>
        <div className="text-right">
          <div className="br-overline" style={{ color: "var(--br-text-dim)" }}>{t("dash.net_rate")}</div>
          <div className="font-mono-fin text-[1.3rem]" style={{ color: "var(--br-bronze)" }}>
            {formatNumber(d.net_rate_pct, 2, locale)}%
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="relative h-px w-full" style={{ background: "var(--br-line-bright)" }}>
          <div className="absolute left-0 top-0 h-px" style={{ width: `${pct}%`, background: "var(--br-bronze)" }} />
        </div>
        <div className="flex justify-between mt-3 text-[0.7rem] font-mono-fin tracking-[0.12em] text-[color:var(--br-text-dim)]">
          <span>{formatDate(d.start_date, locale)}</span>
          <span>{t("dash.days_progress", { elapsed: d.days_elapsed, total: d.duration_days })}</span>
          <span>{formatDate(d.maturity_date, locale)}</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <div className="br-overline">{t("dash.accrued")}</div>
          <div className="font-mono-fin text-[1.05rem] mt-2" style={{ color: "var(--br-bronze-soft)" }}>
            + {formatCurrencyEUR(d.accrued_net, locale)}
          </div>
        </div>
        <div>
          <div className="br-overline">{t("dash.daily")}</div>
          <div className="font-mono-fin text-[1.05rem] mt-2 text-[color:var(--br-parchment)]">
            {formatCurrencyEUR(d.daily_net, locale)}
          </div>
        </div>
      </div>

      {d.auto_reinvest && (
        <div
          className="mt-4 inline-block font-mono-fin text-[0.62rem] tracking-[0.24em] uppercase border px-2 py-1"
          style={{ borderColor: "var(--br-bronze)", color: "var(--br-bronze)" }}
        >
          {t("dash.auto_reinvest_active")}
        </div>
      )}
    </div>
  );
}
